'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from 'react';

// Sanitize string to remove invalid characters that cause DOMException in some browsers
function sanitizeForDOM(str: string): string {
  if (!str) return '';
  try {
    // Remove null bytes, control characters, and other problematic Unicode characters
    // eslint-disable-next-line no-control-regex
    return str.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '').replace(/[\x80-\x9F]/g, '').replace(/\uFFFE|\uFFFF/g, '');
  } catch {
    return '';
  }
}
interface TweetEmbedProps {
  embedJs: string;
}

// Extract tweet ID and URL from embed HTML
function extractTweetInfo(html: string): {
  tweetId: string | null;
  tweetUrl: string | null;
} {
  const match = html.match(/(?:twitter\.com|x\.com)\/(\w+)\/status\/(\d+)/);
  if (match) {
    return {
      tweetId: match[2],
      tweetUrl: `https://x.com/${match[1]}/status/${match[2]}`
    };
  }
  return {
    tweetId: null,
    tweetUrl: null
  };
}

// Skeleton component for tweet loading state
const TweetSkeleton = () => {
  const $ = _c(6);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="w-12 h-12 bg-gray-200 rounded-full" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex items-center gap-3 mb-3">{t0}<div className="flex-1"><div className="h-4 bg-gray-200 rounded w-32 mb-2" /><div className="h-3 bg-gray-200 rounded w-24" /></div><div className="w-6 h-6 bg-gray-200 rounded" /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="space-y-2 mb-3"><div className="h-4 bg-gray-200 rounded w-full" /><div className="h-4 bg-gray-200 rounded w-full" /><div className="h-4 bg-gray-200 rounded w-3/4" /></div>;
    t3 = <div className="h-48 bg-gray-200 rounded-xl mb-3" />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="h-3 bg-gray-200 rounded w-32" />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="mb-4 p-4 border border-gray-200 rounded-xl bg-white animate-pulse">{t1}{t2}{t3}<div className="flex items-center justify-between pt-3 border-t border-gray-100">{t4}<div className="flex gap-4"><div className="h-5 w-5 bg-gray-200 rounded" /><div className="h-5 w-5 bg-gray-200 rounded" /><div className="h-5 w-5 bg-gray-200 rounded" /></div></div></div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
};
export default function TweetEmbed(t0) {
  const $ = _c(18);
  const {
    embedJs
  } = t0;
  const containerRef = useRef(null);
  const [status, setStatus] = useState("loading");
  const [tweetUrl, setTweetUrl] = useState(null);
  const hasInitializedRef = useRef(false);
  let t1;
  if ($[0] !== embedJs || $[1] !== status) {
    t1 = () => {
      if (!embedJs || !containerRef.current || hasInitializedRef.current) {
        return;
      }
      hasInitializedRef.current = true;
      try {
        let decodedContent;
        try {
          decodedContent = atob(embedJs);
        } catch {
          try {
            decodedContent = decodeURIComponent(embedJs);
          } catch {
            decodedContent = embedJs;
          }
        }
        const sanitizedContent = sanitizeForDOM(decodedContent);
        const {
          tweetId,
          tweetUrl: url
        } = extractTweetInfo(sanitizedContent);
        setTweetUrl(url);
        if (!tweetId) {
          setStatus("failed");
          return;
        }
        const renderTweet = () => {
          if (!window.twttr?.widgets || !containerRef.current) {
            return false;
          }
          window.twttr.widgets.createTweet(tweetId, containerRef.current, {
            align: "center"
          }).then(el => {
            setStatus(el ? "loaded" : "failed");
          }).catch(() => {
            setStatus("failed");
          });
          return true;
        };
        if (renderTweet()) {
          return;
        }
        const existingScript = document.querySelector("script[src*=\"platform.twitter.com/widgets.js\"]");
        if (!existingScript) {
          const script = document.createElement("script");
          script.src = "https://platform.twitter.com/widgets.js";
          script.charset = "utf-8";
          script.async = true;
          script.onerror = () => setStatus("failed");
          document.head.appendChild(script);
        }
        const checkTwttr = setInterval(() => {
          if (window.twttr?.widgets) {
            clearInterval(checkTwttr);
            renderTweet();
          }
        }, 200);
        const timeout = setTimeout(() => {
          clearInterval(checkTwttr);
          if (status === "loading") {
            setStatus("failed");
          }
        }, 10000);
        return () => {
          clearInterval(checkTwttr);
          clearTimeout(timeout);
        };
      } catch {
        setStatus("failed");
      }
    };
    $[0] = embedJs;
    $[1] = status;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== embedJs) {
    t2 = [embedJs];
    $[3] = embedJs;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== status) {
    t3 = status === "loading" && <TweetSkeleton />;
    $[5] = status;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== status || $[8] !== tweetUrl) {
    t4 = status === "failed" && tweetUrl && <div className="p-4 border border-gray-200 rounded-xl bg-white"><a href={tweetUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-blue-600 hover:text-blue-800"><svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 flex-shrink-0"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg><span>View post on X</span></a></div>;
    $[7] = status;
    $[8] = tweetUrl;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== status) {
    t5 = status !== "loaded" ? {
      position: "absolute",
      width: 0,
      height: 0,
      overflow: "hidden",
      opacity: 0
    } : undefined;
    $[10] = status;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t5) {
    t6 = <div ref={containerRef} style={t5} />;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t3 || $[15] !== t4 || $[16] !== t6) {
    t7 = <div className="mb-4">{t3}{t4}{t6}</div>;
    $[14] = t3;
    $[15] = t4;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
}